import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { KeyboardKey } from '@this+that/utils';
import {
  LuChevronRight,
  LuCircleAlert,
  LuCircleCheck,
  LuTriangleAlert,
  LuX,
} from 'react-icons/lu';

import {
  STATUS_TOAST_DEFAULT_TEST_ID,
  STATUS_TOAST_DURATION_MS,
  StatusToastActionVariant,
  StatusToastVariant,
} from '../../constants/statusToast';

export interface StatusToastProps {
  status: StatusToastVariant;
  message: string;
  title?: string;
  action?: {
    label: string;
    icon?: ReactNode;
    onClick: () => void;
    variant?: StatusToastActionVariant;
  };
  onClose?: () => void;
  durationMs?: number;
  dataTestId?: string;
}

const STATUS_ICON_COLOR: Record<StatusToastVariant, string> = {
  [StatusToastVariant.Success]: 'text-semantic-status-success',
  [StatusToastVariant.Error]: 'text-semantic-status-error',
  [StatusToastVariant.Warning]: 'text-semantic-status-warning',
  [StatusToastVariant.Loading]: 'text-semantic-status-loading',
  [StatusToastVariant.Undo]: 'text-semantic-status-success',
};

const STATUS_ACCENT_COLOR: Record<StatusToastVariant, string> = {
  [StatusToastVariant.Success]: 'bg-semantic-status-success',
  [StatusToastVariant.Error]: 'bg-semantic-status-error',
  [StatusToastVariant.Warning]: 'bg-semantic-status-warning',
  [StatusToastVariant.Loading]: 'bg-semantic-status-loading',
  [StatusToastVariant.Undo]: 'bg-semantic-status-success',
};

const STATUS_ACTION_COLOR: Record<StatusToastVariant, string> = {
  [StatusToastVariant.Success]:
    'text-semantic-status-success border-semantic-status-success hover:bg-semantic-bg-hover',
  [StatusToastVariant.Error]:
    'text-semantic-status-error border-semantic-status-error hover:bg-semantic-bg-hover',
  [StatusToastVariant.Warning]:
    'text-semantic-status-warning border-semantic-status-warning hover:bg-semantic-bg-hover',
  [StatusToastVariant.Loading]:
    'text-semantic-status-loading border-semantic-status-loading hover:bg-semantic-bg-hover',
  [StatusToastVariant.Undo]:
    'text-semantic-text-accent border-transparent hover:border-semantic-border hover:bg-semantic-bg-hover dark:hover:bg-semantic-bg-subtle',
};

const stylesheet = {
  container:
    'relative flex flex-row items-center gap-3 px-[14px] pt-2 pb-[10px] rounded-semantic min-h-12 overflow-hidden shadow-xl bg-semantic-bg-elevated text-semantic-text-primary border border-semantic-border max-w-[380px]',
  containerClickable: 'group cursor-pointer',
  textGroup: 'flex flex-col flex-1 min-w-0',
  title: 'text-base font-bold leading-tight break-words',
  message: 'text-sm font-medium line-clamp-2 break-words',
  subtitle:
    'text-xs font-medium text-semantic-text-accent line-clamp-1 break-words',
  actionButton:
    'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-transparent border rounded-semantic transition-colors flex-shrink-0 cursor-pointer',
  chevron:
    'flex-shrink-0 text-semantic-text-secondary group-hover:text-semantic-text-accent transition-colors',
  chevronWrapper: 'flex items-center justify-center min-w-8 min-h-8',
  divider: 'w-px h-4 bg-semantic-bg-subtle',
  closeButton:
    'flex items-center justify-center flex-shrink-0 min-w-8 min-h-8 rounded-semantic bg-transparent border-none text-semantic-text-secondary hover:bg-semantic-bg-hover hover:text-semantic-text-primary dark:hover:bg-semantic-bg-subtle active:bg-semantic-bg-subtle transition-colors cursor-pointer',
  progressTrack:
    'absolute bottom-0 left-0 right-0 h-[3px] bg-semantic-bg-hover overflow-hidden',
  progressBar: 'h-full w-full origin-left animate-toast-countdown',
  shimmerTrack:
    'absolute bottom-0 left-0 right-0 h-[3px] bg-semantic-bg-hover overflow-hidden',
  shimmerBar: 'absolute top-0 h-full w-[40%] animate-toast-shimmer',
  spinner:
    'w-[18px] h-[18px] rounded-full border-2 border-current border-t-transparent animate-spin flex-shrink-0',
  actionGroup: 'flex items-center gap-1 flex-shrink-0',
};

function StatusIcon({ status }: { status: StatusToastVariant }) {
  const iconColor = STATUS_ICON_COLOR[status];

  if (status === StatusToastVariant.Loading) {
    return <div className={`${stylesheet.spinner} ${iconColor}`} />;
  }

  if (
    status === StatusToastVariant.Success ||
    status === StatusToastVariant.Undo
  ) {
    return <LuCircleCheck className={iconColor} />;
  }

  if (status === StatusToastVariant.Error) {
    return <LuCircleAlert className={iconColor} />;
  }

  return <LuTriangleAlert className={iconColor} />;
}

function AccentBar({
  status,
  durationMs,
}: {
  status: StatusToastVariant;
  durationMs: number;
}) {
  const accentColor = STATUS_ACCENT_COLOR[status];

  if (status === StatusToastVariant.Loading) {
    return (
      <div className={stylesheet.shimmerTrack}>
        <div className={`${stylesheet.shimmerBar} ${accentColor}`} />
      </div>
    );
  }

  return (
    <div className={stylesheet.progressTrack}>
      <div
        className={`${stylesheet.progressBar} ${accentColor}`}
        style={
          {
            '--toast-countdown-duration': `${durationMs}ms`,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

export function StatusToast({
  status,
  message,
  title,
  action,
  onClose,
  durationMs = STATUS_TOAST_DURATION_MS,
  dataTestId,
}: Readonly<StatusToastProps>) {
  const { t } = useTranslation();
  const isClickable = action?.variant === StatusToastActionVariant.Clickable;

  const handleAction = () => {
    action?.onClick();
    onClose?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === KeyboardKey.ENTER || e.key === KeyboardKey.SPACE) {
      e.preventDefault();
      handleAction();
    }
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose?.();
  };

  return (
    <div
      className={`${stylesheet.container} ${isClickable ? stylesheet.containerClickable : ''}`}
      data-testid={dataTestId ?? STATUS_TOAST_DEFAULT_TEST_ID}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? handleAction : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
    >
      <StatusIcon status={status} />
      <div className={stylesheet.textGroup}>
        {title && <span className={stylesheet.title}>{title}</span>}
        <span className={stylesheet.message}>{message}</span>
        {isClickable && (
          <span className={stylesheet.subtitle}>{action?.label}</span>
        )}
      </div>
      {(action || onClose) && (
        <div className={stylesheet.actionGroup}>
          {isClickable ? (
            <>
              <div className={stylesheet.chevronWrapper}>
                <LuChevronRight className={stylesheet.chevron} />
              </div>
              {onClose && <div className={stylesheet.divider} />}
            </>
          ) : (
            action && (
              <button
                type="button"
                className={`${stylesheet.actionButton} ${STATUS_ACTION_COLOR[status]}`}
                onClick={handleAction}
                data-testid="status-toast-action-button"
              >
                {action.icon}
                {action.label}
              </button>
            )
          )}
          {onClose && (
            <button
              type="button"
              className={stylesheet.closeButton}
              onClick={handleCloseClick}
              aria-label={t('aria-labels.close')}
              data-testid="status-toast-close-button"
            >
              <LuX />
            </button>
          )}
        </div>
      )}
      <AccentBar status={status} durationMs={durationMs} />
    </div>
  );
}
